import React, { useEffect, useState, useMemo } from "react";
import { api } from "@/lib/api";

export default function RhythmSparkline() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    api
      .get("/grove/rhythm?days=30")
      .then((r) => setData(r.data))
      .catch(() => setData(null))
      .finally(() => setLoading(false));
  }, []);

  const { bars, maxWeight, deltaText, deltaColor } = useMemo(() => {
    if (!data || !data.bins?.length)
      return { bars: [], maxWeight: 1, deltaText: "", deltaColor: "#526357" };
    const max = Math.max(1, ...data.bins.map((b) => b.weight));
    const bars = data.bins;
    const prev = data.prev_total_events || 0;
    const cur = data.total_events || 0;
    let txt = "";
    let color = "#526357";
    if (prev === 0 && cur === 0) {
      txt = "the grove rests";
    } else if (prev === 0) {
      txt = "first breath";
      color = "#B85C38";
    } else {
      const diff = cur - prev;
      const pct = Math.round((diff / prev) * 100);
      if (pct > 10) {
        txt = `+${pct}% vs last month`;
        color = "#869960";
      } else if (pct < -10) {
        txt = `${pct}% vs last month`;
        color = "#6C5943";
      } else {
        txt = "steady rhythm";
        color = "#526357";
      }
    }
    return { bars, maxWeight: max, deltaText: txt, deltaColor: color };
  }, [data]);

  if (loading || !data) return null;

  return (
    <div
      className="mt-6 pt-5 border-t border-[#e4ddc9]"
      data-testid="rhythm-sparkline"
    >
      <div className="flex items-baseline justify-between gap-3 flex-wrap mb-3">
        <div className="text-[10px] tracking-[0.3em] uppercase text-[#6C5943]">
          30-day rhythm
        </div>
        <div
          className="text-xs italic"
          style={{ color: deltaColor }}
          data-testid="rhythm-delta"
        >
          {deltaText}
        </div>
      </div>
      <div
        className="flex items-end gap-[3px] h-12"
        aria-label="30 day activity rhythm"
        role="img"
      >
        {bars.map((b) => {
          const h = b.weight === 0 ? 2 : Math.max(3, (b.weight / maxWeight) * 48);
          const isToday = b.date === new Date().toISOString().slice(0, 10);
          return (
            <div
              key={b.date}
              className="flex-1 rounded-sm transition-all hover:opacity-100 opacity-80"
              style={{
                height: `${h}px`,
                background: b.weight > 0 ? "#B85C38" : "#d8d3c4",
                boxShadow: isToday ? "0 0 0 1px #1C241F inset" : "none",
              }}
              title={`${b.date}: ${b.count} event${b.count === 1 ? "" : "s"}`}
            />
          );
        })}
      </div>
      <div className="flex justify-between mt-1.5 text-[10px] text-[#9FA9A3]">
        <span>30 days ago</span>
        <span>today</span>
      </div>
    </div>
  );
}
